/**
 * ``useComponentConnections`` — lazy per-connection list for a connector
 * component's detail pane (Flow L Frame 3; ADR-035 / S6; REQ-STATUS-021).
 *
 * Fetches ``GET /api/system/components/{component_id}/connections`` only
 * when a connections component is selected (``componentId`` non-null) — the
 * storyboard's lazy load (the roll-up connection nodes carry only a rollup +
 * count; the per-connection colours load on drill-in). Bounded server-side at
 * the 1,000-connection ceiling (REQ-STATUS-021); the detail-pane list
 * virtualizes / groups / sorts worst-first (OQ-3 resolved).
 *
 * **Every authenticated role** (REQ-STATUS-029 as widened by ADR-036 §D-7 /
 * REQ-PEER-042): the hook mirrors the BFF gate (defence-in-depth) and is
 * additionally disabled when no component is selected. Shape-guarded at the
 * trust boundary (coding-standards §2.2).
 *
 * Cross-references:
 *   - Flow L §5 (connection detail); REQ-STATUS-021 (connections endpoint).
 *   - REQ-STATUS-029 (RBAC); REQ-PEER-042 (all-roles read); REQ-UI-035
 *     (BFF-only).
 */
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useSession } from "@/auth/session-context";
import type { ComponentConnections } from "@/system-status/status-model";
import {
  SNAPSHOT_MIN_REFRESH_MS,
  SYSTEM_STATUS_READER_ROLES,
} from "@/system-status/use-system-status-snapshot";
import { useLiveFrameRefresh } from "@/system-status/use-live-frame-refresh";

/** The TanStack Query key for one component's connection list. */
export function componentConnectionsKey(componentId: string): readonly unknown[] {
  return ["system-status", "connections", componentId];
}

/**
 * Fetch the connection list for the selected connector component. Disabled
 * (no request) when ``componentId`` is null or the session is not authorized.
 * 401/403 are not retried.
 */
export function useComponentConnections(
  componentId: string | null,
): UseQueryResult<ComponentConnections, ApiError> {
  const session = useSession();
  const enabled = componentId !== null && session.hasAnyRole(SYSTEM_STATUS_READER_ROLES);
  const query = useQuery<ComponentConnections, ApiError>({
    queryKey: componentConnectionsKey(componentId ?? ""),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(
        `/api/system/components/${encodeURIComponent(componentId ?? "")}/connections`,
        { signal },
      );
      if (!isConnections(raw)) {
        console.error(
          "[meridian/system-status/use-component-connections] response failed shape validation",
          raw,
        );
        throw new ApiError(
          500,
          raw,
          "system-status connections response did not match the expected shape",
        );
      }
      return raw;
    },
    enabled,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // This roster carries the most volatile numbers on the display — bytes/s,
  // msgs/s, error and dropped counts, last-message time — and before
  // REQ-STATUS-099 it had no refresh path at all: no interval, and (unlike the
  // snapshot) not even an invalidation on SSE reconnect. It was frozen for as
  // long as the selection and the cache entry lived.
  useLiveFrameRefresh({
    queryKey: componentConnectionsKey(componentId ?? ""),
    refetch: query.refetch,
    minIntervalMs: SNAPSHOT_MIN_REFRESH_MS,
    enabled,
  });

  return query;
}

/** Trust-boundary shape guard (coding-standards §2.2). */
function isConnections(value: unknown): value is ComponentConnections {
  if (typeof value !== "object" || value === null) return false;
  const o = value as Record<string, unknown>;
  return typeof o.component_id === "string" && Array.isArray(o.connections);
}
